/**
 * Registry merge strategy (spec §07 §5.1)
 *
 * Implements a CvRDT join-semilattice merge for the workspace registry.
 * Properties:
 *   - Commutative:  merge(A, B) = merge(B, A)  (workspaces, MCP, tombstones)
 *   - Idempotent:   merge(A, A) = A
 *   - Monotonic:    merged ⊇ both inputs (add-wins / no data loss)
 *
 * Note: Preferences merge is intentionally asymmetric per spec §5.1:
 * "remote vinner" — the second argument wins for overlapping keys.
 * This means merge(A, B) ≠ merge(B, A) for preferences.
 */

import type { WorkspaceEntry, McpConnectionEntry } from './types'

// ── Registry State ──────────────────────────────────────────────

export interface RegistryState {
    v: '1'
    type: 'secureblob-registry'
    updatedAt: string       // ISO 8601
    workspaces: RegistryWorkspaceEntry[]
    mcpConnections: McpConnectionEntry[]
    preferences: Record<string, unknown>
    /** List of blobIds that have been permanently deleted from this registry.
     * Prevents Add-Wins merge from re-introducing deleted workspaces. */
    tombstonedIds?: string[]
}

/** Extended workspace entry with timestamps for merge ordering */
export interface RegistryWorkspaceEntry extends WorkspaceEntry {
    addedAt?: string        // ISO 8601
    lastOpenedAt?: string   // ISO 8601
    role?: string
    writerKids?: string[]
    pinned?: boolean
    order?: number
    color?: string
}

// ── MergeStrategy interface ─────────────────────────────────────

export interface MergeStrategy<T> {
    /** Join two states into a merged result (least upper bound) */
    merge(local: T, remote: T): T
    /** Default empty state */
    empty(): T
}

// ── Registry merge implementation ───────────────────────────────

function isoMax(a?: string, b?: string): string | undefined {
    if (!a) return b
    if (!b) return a
    return a >= b ? a : b
}

function isoMin(a?: string, b?: string): string | undefined {
    if (!a) return b
    if (!b) return a
    return a <= b ? a : b
}

/**
 * Merge two workspace entry arrays. Union on blobId, LWW by lastOpenedAt.
 * Add-wins: entries present on either side are kept.
 * Tombstone-aware: entries in tombstonedIds are dropped.
 */
function mergeWorkspaces(
    local: RegistryWorkspaceEntry[],
    remote: RegistryWorkspaceEntry[],
    tombstonedIds: Set<string>
): RegistryWorkspaceEntry[] {
    const map = new Map<string, RegistryWorkspaceEntry>()

    // Merge helper
    const processEntry = (entry: RegistryWorkspaceEntry) => {
        if (tombstonedIds.has(entry.blobId)) return

        const existing = map.get(entry.blobId)
        if (!existing) {
            map.set(entry.blobId, { ...entry })
        } else {
            // LWW by latest timestamp
            const localTime = existing.lastOpenedAt ?? existing.addedAt ?? ''
            const remoteTime = entry.lastOpenedAt ?? entry.addedAt ?? ''

            if (remoteTime > localTime) {
                map.set(entry.blobId, {
                    ...entry,
                    addedAt: isoMin(existing.addedAt, entry.addedAt),
                    lastOpenedAt: isoMax(existing.lastOpenedAt, entry.lastOpenedAt),
                })
            } else {
                map.set(entry.blobId, {
                    ...existing,
                    addedAt: isoMin(existing.addedAt, entry.addedAt),
                    lastOpenedAt: isoMax(existing.lastOpenedAt, entry.lastOpenedAt),
                })
            }
        }
    }

    for (const entry of local) processEntry(entry)
    for (const entry of remote) processEntry(entry)

    return Array.from(map.values())
}

/**
 * Merge two MCP connection arrays. Union on configBlobId, LWW by createdAt.
 */
function mergeMcpConnections(
    local: McpConnectionEntry[],
    remote: McpConnectionEntry[],
    tombstonedIds: Set<string>
): McpConnectionEntry[] {
    const map = new Map<string, McpConnectionEntry>()

    for (const entry of local) {
        if (tombstonedIds.has(entry.configBlobId)) continue
        map.set(entry.configBlobId, { ...entry })
    }

    for (const entry of remote) {
        if (tombstonedIds.has(entry.configBlobId)) continue
        const existing = map.get(entry.configBlobId)
        if (!existing) {
            map.set(entry.configBlobId, { ...entry })
        } else {
            if (entry.createdAt > existing.createdAt) {
                map.set(entry.configBlobId, { ...entry })
            }
        }
    }

    return Array.from(map.values())
}

/**
 * Merge preferences — remote wins for all keys.
 * But local-only keys are preserved (add-wins).
 */
function mergePreferences(
    local: Record<string, unknown>,
    remote: Record<string, unknown>,
): Record<string, unknown> {
    return { ...local, ...remote }
}

// ── Exported merge strategy ─────────────────────────────────────

export const registryMerge: MergeStrategy<RegistryState> = {
    merge(local: RegistryState, remote: RegistryState): RegistryState {
        // Tombstones union survives (monotonic)
        const tombstones = new Set([
            ...(local.tombstonedIds ?? []),
            ...(remote.tombstonedIds ?? [])
        ])

        return {
            v: '1',
            type: 'secureblob-registry',
            updatedAt: isoMax(local.updatedAt, remote.updatedAt) ?? new Date().toISOString(),
            workspaces: mergeWorkspaces(local.workspaces ?? [], remote.workspaces ?? [], tombstones),
            mcpConnections: mergeMcpConnections(local.mcpConnections ?? [], remote.mcpConnections ?? [], tombstones),
            preferences: mergePreferences(local.preferences ?? {}, remote.preferences ?? {}),
            tombstonedIds: Array.from(tombstones)
        }
    },

    empty(): RegistryState {
        return {
            v: '1',
            type: 'secureblob-registry',
            updatedAt: new Date().toISOString(),
            workspaces: [],
            mcpConnections: [],
            preferences: {},
            tombstonedIds: []
        }
    },
}

